import {
    Action,
    action, Computed, computed, Thunk, thunk,
} from 'easy-peasy';
import memoize from 'memoize-one';
import {Storyboard} from '@/entities/storyboard';
import {StoreModel} from '@/store';


export interface ActiveStoryboardModel {
    activeHashid: string;
    activeDummyStoryboardUrl: string;

    activeIndex: Computed<ActiveStoryboardModel, number, StoreModel>;
    activeId: Computed<ActiveStoryboardModel, number, StoreModel>;
    activeStoryboard: Computed<ActiveStoryboardModel, Storyboard, StoreModel>;
    setActiveStoryboard: Thunk<ActiveStoryboardModel, [string, string], null, StoreModel>;
    setActiveStoryboardIds: Action<ActiveStoryboardModel, [string, string]>;
}

export default <ActiveStoryboardModel>{
    activeHashid: '',
    activeDummyStoryboardUrl: '',

    activeIndex: computed(
        [
            (state) => state.activeHashid,
            (state) => state.activeDummyStoryboardUrl,
            (state, storeState) => storeState.storyboards.items,
        ],
        // @ts-ignore
        memoize(
            (hashid, dummyStoryboardUrl, storyboards) => {
                // Safety check: ensure storyboards is an array
                if (!Array.isArray(storyboards)) {
                    return -1;
                }

                let activeStoryboardIndex;

                if (hashid) {
                    activeStoryboardIndex = storyboards.findIndex((sb: Storyboard) => sb.hashid === hashid);
                } else {
                    activeStoryboardIndex = storyboards.findIndex((sb: Storyboard) => sb.slug === decodeURIComponent(dummyStoryboardUrl));
                }

                return activeStoryboardIndex;
            },
        ),
    ),
    activeId: computed(
        [
            (state) => state.activeHashid,
            (state) => state.activeDummyStoryboardUrl,
            (state, storeState) => storeState.storyboards.items,
        ],
        // @ts-ignore
        memoize(
            (hashid, dummyStoryboardUrl, storyboards) => {
                // Safety check: ensure storyboards is an array
                if (!Array.isArray(storyboards)) {
                    return null;
                }

                let activeStoryboard;

                if (hashid) {
                    activeStoryboard = storyboards.find((sb: Storyboard) => sb.hashid === hashid);
                } else {
                    activeStoryboard = storyboards.find((sb: Storyboard) => sb.slug === dummyStoryboardUrl);
                }

                if (!activeStoryboard) {
                    return null;
                }

                return activeStoryboard.id;
            },
        ),
    ),
    activeStoryboard: computed(
        [
            (state) => state.activeIndex,
            (state, storeState) => storeState.storyboards.items,
        ],
        (activeIndex, storyboards) => {
            // Safety checks: ensure storyboards is an array and activeIndex is valid
            if (!Array.isArray(storyboards) || activeIndex < 0 || activeIndex >= storyboards.length) {
                return null;
            }
            return storyboards[activeIndex];
        },
    ),
    setActiveStoryboard: thunk((actions, payload, {getState, getStoreActions}) => {
        actions.setActiveStoryboardIds(payload);

        const {activeStoryboard} = getState();
        if (activeStoryboard?.globalFilters) {
            getStoreActions().search.setSearchQueryAllFields(activeStoryboard.globalFilters.all_fields);
            getStoreActions().search.setSearchQueryAdditionalFilters(activeStoryboard.globalFilters.additional_filters);
        }
    }),
    setActiveStoryboardIds: action((state, [activeHashid, activeDummySlug]) => {
        state.activeHashid = activeHashid;
        state.activeDummyStoryboardUrl = activeDummySlug;
    }),
};
